import { zodResolver } from "@hookform/resolvers/zod";
import {
    getAllCountries,
    getAllTimezones,
    getTimezonesForCountry,
} from "countries-and-timezones";
import fileDownload from "js-file-download";
import React from "react";
import { Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import {
    FaDownload,
    FaExclamationTriangle,
    FaMap,
    FaSave,
    FaUpload,
} from "react-icons/fa";
import { MdBuild } from "react-icons/md";
import { PiFlowerTulipFill } from "react-icons/pi";
import { Link, Navigate, useParams } from "react-router";
import { z } from "zod";

import * as api from "../../api/api";
import {
    LocationDocument,
    useAreasDocument,
    useFlyzonesDocument,
    useLocationDocument,
    useMarkersDocument,
    useMissionHandlerClusterCollection,
    useOrganizationDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CancelButton from "../../components/CancelButton";
import BooleanFormGroup from "../../components/FormGroups/BooleanFormGroup.jsx";
import ReactSelectFormGroup from "../../components/FormGroups/ReactSelectFormGroup.jsx";
import SelectFormGroup from "../../components/FormGroups/SelectFormGroup.jsx";
import TextFormGroup from "../../components/FormGroups/TextFormGroup.jsx";
import {
    ConfigDownloadJSONButton,
    ConfigUploadJSONButton,
} from "../../components/JsonButtons/index.jsx";
import Layout, { Title } from "../../components/Layout/Layout";
import TimeDisplay from "../../components/TimeDisplay/index.jsx";
import { useUser } from "../../contexts/user_provider";
import { SPECIAL_UI_ACCESS_ORGANIZATION_ID } from "../../organizations.js";
import urls from "../../urls.jsx";

export function ConfigDownloadCSVButton(props) {
    async function onDownload() {
        const csvData = await props.onDownload();
        fileDownload(csvData, `${props.name}.csv`);
    }

    return (
        <div className={`d-grid gap-2 ${props.className || ""}`}>
            <Button
                disabled={props.disabled}
                variant="outline-secondary"
                onClick={onDownload}
            >
                <FaDownload className="text-success me-2" />
                {props?.buttonText || "CSV Download"}
            </Button>
        </div>
    );
}

export function ConfigUploadCSVButton({
    buttonText,
    disabled,
    onUpload,
    className,
    addFilename,
    onStart,
    startUploadRequest,
    onStartUploadHandled,
}) {
    const inputFile = React.useRef(null);
    let fileReader;
    const [error, setError] = React.useState(null);
    const [showModal, setShowModal] = React.useState(false);

    const handleFileRead = async (filename) => {
        const fileText = fileReader.result;
        try {
            if (addFilename) {
                console.log("add filename");
                await onUpload(fileText, filename);
            } else {
                await onUpload(fileText);
            }
        } catch (err) {
            setError(err.message);
            setShowModal(true);
        }

        // Clear the file input value after handling the file read (needed for Chrome)
        inputFile.current.value = null;
    };

    const handleFileChosen = (file) => {
        if (!file.name.endsWith(".csv")) {
            setError("Invalid file format. Please upload a *.csv file.");
            setShowModal(true);
            return;
        }

        fileReader = new FileReader();
        fileReader.onloadend = () => handleFileRead(file.name);
        fileReader.readAsText(file);
    };

    const onButtonClick = () => {
        if (onStart) {
            onStart();
            return;
        }

        inputFile.current.click();
    };

    React.useEffect(() => {
        if (!startUploadRequest) return;

        inputFile.current?.click();
        onStartUploadHandled?.();
    }, [startUploadRequest, onStartUploadHandled]);

    const handleClose = () => {
        setShowModal(false);
    };

    return (
        <div className={`d-grid gap-2 ${className || ""}`}>
            <Form.Control
                type="file"
                style={{ display: "none" }}
                ref={inputFile}
                onChange={(e) => handleFileChosen(e.target.files[0])}
            />
            <Button
                disabled={disabled}
                variant="outline-secondary"
                onClick={onButtonClick}
            >
                <FaUpload className="text-danger me-2" />
                {buttonText || "CSV Upload"}
            </Button>

            <Modal show={showModal} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaExclamationTriangle className="me-2" />
                        Error
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error?.split("\n").map((line, idx) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: Error lines are positional, may contain duplicates, and have no stable identifier.
                        <React.Fragment key={idx}>
                            {line}
                            <br />
                        </React.Fragment>
                    ))}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

function LocationSettings(props) {
    const [isLoading, setIsLoading] = React.useState(false);
    const [timeZoneOptions, setTimeZoneOptions] = React.useState([]);
    const [countryOptions, setCountryOptions] = React.useState([]);
    const [missionHandlerDocs] = useMissionHandlerClusterCollection();
    const missionHandlerVersions = missionHandlerDocs
        .map((doc) => doc.id)
        .filter((version) => version !== "apps");

    const numberFromString = z.coerce.number({
        invalid_type_error: "Enter a valid number",
    });
    const locationSettingsConfigSchema = z.object({
        name: z.string().min(1, { message: "Name is required" }),
        markerType: z.enum(["ARGUS", "ARUCO"]),
        country: z.string(),
        timezone: z.string().refine((value) => value !== "none", {
            message: "Choose a time zone",
        }),
        missionHandler: z.object({
            version: z.string().min(1, { message: "Version is required" }),
            maxAutonomousFlights: numberFromString.nonnegative(),
        }),
        visualization: z.object({
            showDiskSpaceOnDronePage: z.boolean(),
        }),
        mobileDock: z.object({
            takeoffHeight: numberFromString.positive(),
        }),
    });

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isDirty, isSubmitting },
    } = useForm({
        resolver: zodResolver(locationSettingsConfigSchema),
    });

    const onSubmit = async (locationSettingsUpdate) => {
        await LocationDocument(props.organizationId, props.locationId).update(
            locationSettingsUpdate,
        );
        reset(locationSettingsUpdate); // Reset the form with the latest saved state
    };

    React.useEffect(() => {
        if (props.location) {
            setIsLoading(false);
            reset(props.location);
        } else {
            setIsLoading(true);
        }
    }, [props.location, reset]);

    const selectedCountry = watch("country");
    const selectedTimezone = watch("timezone");

    React.useEffect(() => {
        const timezoneList = Object.values(
            getTimezonesForCountry(selectedCountry) ?? getAllTimezones(),
        ).flatMap((timezone) => ({
            id: timezone.name,
            name:
                timezone.utcOffset !== timezone.dstOffset
                    ? `${timezone.name} (UTC${timezone.utcOffsetStr} / ${timezone.dstOffsetStr})`
                    : `${timezone.name} (UTC${timezone.utcOffsetStr})`,
        }));
        if (
            selectedCountry === "NL" ||
            selectedCountry === undefined ||
            selectedCountry === "Unknown"
        ) {
            timezoneList.push({
                id: "Europe/Amsterdam",
                name: "Europe/Amsterdam (UTC+01:00 / +02:00)",
            });
        }
        timezoneList.sort((a, b) => a.name.localeCompare(b.name));
        setTimeZoneOptions(timezoneList);

        const foundtimezone = timezoneList.find(
            (m) => m.id === selectedTimezone,
        );
        if (foundtimezone === undefined) {
            if (timezoneList.length !== 1) {
                setValue("timezone", "none");
            } else {
                setValue("timezone", timezoneList[0].id);
            }
        }
    }, [selectedCountry, setValue, selectedTimezone]);

    React.useEffect(() => {
        const options = Object.values(getAllCountries())
            .map((country) => ({
                name: country.name,
                id: country.id,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

        options.unshift({
            id: "Unknown",
            name: "Unknown",
        });

        setCountryOptions(options);
    }, []);

    return (
        <Card.Body className="py-2">
            {isLoading ? (
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            ) : (
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <TextFormGroup
                        label="Name"
                        name="name"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                    />

                    <SelectFormGroup
                        label="Marker Type"
                        name="markerType"
                        control={control}
                        errors={errors}
                        defaultValue={location?.markerType || "ARGUS"}
                        setValue={setValue}
                        options={[
                            { id: "ARGUS", name: "ARGUS" },
                            { id: "ARUCO", name: "ARUCO" },
                        ]}
                    />

                    <SelectFormGroup
                        label="Country"
                        name="country"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        options={countryOptions}
                    />

                    <SelectFormGroup
                        label="Time zone"
                        name="timezone"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        disabledOption={{
                            id: "none",
                            name: "Choose a time zone",
                        }}
                        options={timeZoneOptions}
                    />

                    <h6>Mission handler</h6>
                    <ReactSelectFormGroup
                        label="Version"
                        name="missionHandler.version"
                        options={missionHandlerVersions.map((version) => {
                            return { id: version, name: version };
                        })}
                        control={control}
                        errors={errors}
                        defaultValue="production"
                        setValue={setValue}
                    />
                    <TextFormGroup
                        label="Max autonomous flights"
                        name="missionHandler.maxAutonomousFlights"
                        control={control}
                        errors={errors}
                        defaultValue="999"
                        setValue={setValue}
                    />

                    <h6>Visualization</h6>
                    <BooleanFormGroup
                        label="Show disk space on drone page"
                        name="visualization.showDiskSpaceOnDronePage"
                        control={control}
                        errors={errors}
                        defaultValue={false}
                        setValue={setValue}
                    />

                    <h6>Mobile dock (only rpv3)</h6>
                    <TextFormGroup
                        label="Takeoff height"
                        name="mobileDock.takeoffHeight"
                        control={control}
                        errors={errors}
                        defaultValue={0.7}
                        setValue={setValue}
                        unitText={"m"}
                    />
                    <Card.Body className="p-0">
                        <Button
                            variant="success"
                            disabled={isSubmitting || !isDirty}
                            type="submit"
                            className="me-2"
                        >
                            <FaSave className="mb-1 me-1" />
                            {isSubmitting ? "Saving..." : "Save"}
                        </Button>
                        <CancelButton
                            to={urls.location(
                                props.organizationId,
                                props.locationId,
                            )}
                        />
                    </Card.Body>
                </Form>
            )}
        </Card.Body>
    );
}

export default function LocationSettingsPage() {
    const { organizationId, locationId } = useParams();
    const [areas] = useAreasDocument(organizationId, locationId);
    const [markers] = useMarkersDocument(organizationId, locationId);
    const [flyzones] = useFlyzonesDocument(organizationId, locationId);
    const { getUserIsAdmin } = useUser();
    const userIsAdmin = getUserIsAdmin();
    const hasLimitedLocationSettingsAccess =
        organizationId === SPECIAL_UI_ACCESS_ORGANIZATION_ID;

    const [organization] = useOrganizationDocument(organizationId);
    const [location] = useLocationDocument(organizationId, locationId, {
        mode: "once",
    });
    function getAreasCount() {
        if (!areas || !Object.hasOwn(areas, "areas")) {
            return 0;
        }
        const rowKeys = Object.keys(areas.areas);
        if (rowKeys <= 0) return 0;
        const columnKeys = Object.keys(areas.areas[rowKeys[0]]);
        return rowKeys.length * columnKeys.length;
    }

    function getUpdateTime(data) {
        if (data?.updatedAt?.seconds) {
            return (
                <TimeDisplay
                    timestamp={data.updatedAt.seconds}
                    format="YYYY-MM-DD HH:mm:ss"
                    organizationId={organizationId}
                    locationId={locationId}
                    location={location}
                />
            );
        }
        return "N/A";
    }

    if (!userIsAdmin && !hasLimitedLocationSettingsAccess) {
        return (
            <Navigate to={urls.location(organizationId, locationId)} replace />
        );
    }

    return (
        <Layout>
            <title>{`${location?.name} Settings | MyCorvus`}</title>
            <Breadcrumbs
                path={breadcrumbPaths.locationSettings}
                organization={organization}
                location={location}
            />
            <Card className="p-0">
                <Title
                    title={"Location Settings"}
                    backButtonUrl={urls.location(organizationId, locationId)}
                >
                    {userIsAdmin && (
                        <>
                            <Button
                                variant="outline-secondary"
                                as={Link}
                                to={urls.varieties(organizationId, locationId)}
                                className="ms-2 mb-1"
                            >
                                <PiFlowerTulipFill
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                Varieties
                            </Button>
                            <Button
                                variant="outline-secondary"
                                as={Link}
                                to={urls.areaBuilder(
                                    organizationId,
                                    locationId,
                                )}
                                className="ms-2 mb-1"
                            >
                                <MdBuild
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                Area builder
                            </Button>
                        </>
                    )}
                    <Button
                        variant="outline-secondary"
                        as={Link}
                        to={`${urls.locationMap(organizationId, locationId)}${userIsAdmin ? "?inspect" : ""}`}
                        className="ms-2 mb-1"
                    >
                        <FaMap className="me-2" />
                        View Map
                    </Button>
                </Title>
            </Card>
            <Row>
                <Col xs={12} md={4} className="mt-2">
                    <Card>
                        <Card.Body>
                            <Card.Title>
                                Markers
                                {` (${(markers?.markers || []).length})`}
                            </Card.Title>
                            <Card.Subtitle>
                                Updated: {getUpdateTime(markers)}
                            </Card.Subtitle>
                            <ConfigDownloadCSVButton
                                name={`markers_${locationId}`}
                                onDownload={async () =>
                                    await api.getMarkersAsCsv(
                                        organizationId,
                                        locationId,
                                    )
                                }
                                className="mb-2"
                            />
                            <ConfigUploadCSVButton
                                onUpload={async (markersCsv) =>
                                    await api.setMarkersFromCsv(
                                        organizationId,
                                        locationId,
                                        markersCsv,
                                    )
                                }
                            />
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} md={4} className="mt-2">
                    <Card>
                        <Card.Body>
                            <Card.Title>
                                Flyzones
                                {` (${(flyzones?.flyzones || []).length})`}
                            </Card.Title>
                            <Card.Subtitle>
                                Updated: {getUpdateTime(flyzones)}
                            </Card.Subtitle>
                            <ConfigDownloadCSVButton
                                name={`flyzones_${locationId}`}
                                onDownload={async () =>
                                    await api.getFlyzonesAsCsv(
                                        organizationId,
                                        locationId,
                                    )
                                }
                                className="mb-2"
                            />
                            <ConfigUploadCSVButton
                                onUpload={async (flyzonesCsv) => {
                                    await api.setFlyzonesFromCsv(
                                        organizationId,
                                        locationId,
                                        flyzonesCsv,
                                    );
                                }}
                            />
                        </Card.Body>
                    </Card>
                </Col>
                {userIsAdmin && (
                    <Col xs={12} md={4} className="mt-2">
                        <Card>
                            <Card.Body>
                                <Card.Title>
                                    Areas{` (${getAreasCount()})`}
                                </Card.Title>
                                <Card.Subtitle>
                                    Updated: {getUpdateTime(areas)}
                                </Card.Subtitle>
                                <div className="d-grid gap-2">
                                    <ConfigDownloadJSONButton
                                        filename={`areas_${organizationId}_${locationId}`}
                                        onDownload={async () =>
                                            await api.getAreasAsJson(
                                                organizationId,
                                                locationId,
                                            )
                                        }
                                    />
                                    <ConfigUploadJSONButton
                                        onUpload={async (areasJsonString) =>
                                            await api.setAreasFromJson(
                                                organizationId,
                                                locationId,
                                                areasJsonString,
                                            )
                                        }
                                    />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                )}
            </Row>

            {userIsAdmin && (
                <Card className="mt-2">
                    <Card.Body>
                        <LocationSettings
                            location={location}
                            organizationId={organizationId}
                            locationId={locationId}
                        ></LocationSettings>
                    </Card.Body>
                </Card>
            )}
        </Layout>
    );
}
